import { type Address } from "./address";
import { type GeoLocation } from "./util";

interface AgentOnboardingFlags {
  isSignupInitiated?: boolean;
  isSignupCompleted?: boolean;
  signupStage?: string;
}

export interface License {
  number?: string;
  expires?: string;
  state?: string;
}

export interface LicenseData {
  // eslint-disable-next-line @typescript-eslint/naming-convention
  _id: string;
  /**
   * Note that qualifications are dynamic strings from
   * GET /workerAndWorkPlaceTypes endpoint
   * See https://clipboardhealth.atlassian.net/wiki/spaces/TS1/pages/2840397357?search_id=ed50698c-9f73-482b-9c36-1e04ea605b71
   */
  qualification: string;
  expiresAt?: string | null;
  state: string;
  multiState: boolean;
}

export interface AgentAttendance {
  sortMeta?: 0 | 1;
  completedShifts?: number;
  cancelledShifts?: number;
  totalShifts?: number;
  consecutive?: number;
  consecutiveCancels?: number;
  percentage?: number;
  updatedAt?: string;
}

export interface AgentRequirement {
  requirement?: string;
  expires?: string;
}

export interface AgentUploads {
  requirement?: string;
  status?: string;
}

export interface Specialities {
  experience?: string[];
  hasSedationExperience?: boolean;
  hasTrayAssemblyExperience?: boolean;
}

interface AgentRating {
  count?: number;
  value?: number;
}

export interface NotificationPreference {
  /**
   * @deprecated
   * FIXME - Delete this deprecated field
   */
  off: boolean; // deprecated field
  push: boolean;
  email: boolean;
  sms: boolean;
  marketingPush: boolean;
  marketingEmail: boolean;
  marketingSMS: boolean;
  urgentShiftNotification?: {
    isOn: boolean;
  };
  priorityAccessPush?: boolean;
  placementsMarketingPush?: boolean;
}

interface Preference {
  distance?: number;
}

interface AppRating {
  appRatingSubmitted?: boolean;
  countPromptShown?: number;
  appRating?: string;
  countCompletedShiftsWhenLastShownPrompt?: number;
  lastTimePromptWasShown?: Date;
}

interface ContractorAgreement {
  agreementVersion: string;
  signedDate: Date;
}

interface BackgroundCheckApplication {
  applicationType?: string;
  candidateId?: string;
}
interface Mobile {
  openedAt?: Date;
  version?: string;
  build?: number;
  platform?: string;
  isLocationServicesEnabled?: boolean;
  locationServicesPermission?: string;
}

export const AgentStages = {
  ONBOARDING: "ONBOARDING",
  ENROLLED: "ENROLLED",
  PROBATION: "PROBATION",
  RESTRICTED: "RESTRICTED",
  DEACTIVATED: "DEACTIVATED",
} as const;

export type AgentStage = (typeof AgentStages)[keyof typeof AgentStages];

export const PaymentServiceAccountStatuses = {
  ACCOUNT_CREATED: "Account Created",
  ACCOUNT_NEEDS_UPDATE: "Account Needs Updating",
  STANDARD_PAYOUT_PENDING: "Standard Payouts Pending Approval",
  INSTANT_PAYOUT_PENDING: "Instant Payouts Pending Approval",
  STANDARD_PAYOUT_ENABLED: "Standard Payouts Enabled",
  INSTANT_PAYOUT_ENABLED: "Instant Payouts Enabled",
  NO_DOCUMENTS_REQUIRED: "No Documents Required",
} as const;

export type PaymentServiceAccountStatus =
  (typeof PaymentServiceAccountStatuses)[keyof typeof PaymentServiceAccountStatuses];

export interface PaymentAccountInfo {
  // eslint-disable-next-line @typescript-eslint/naming-convention
  _id: string;
  accountId: string;
  createdAt: string;
  enabled: boolean;
  enabledAt: string;
  updatedAt: string;
  autoWithdrawEnabled: boolean;
  withdrawalFee: number;
  status: PaymentServiceAccountStatus;
  isTestAccount: boolean;
  disabledReason: string | undefined;
  gatewayTransferDisabled: boolean;
}

export interface Agent {
  // eslint-disable-next-line @typescript-eslint/naming-convention
  _id?: string;
  active?: boolean;
  userId?: string;
  name?: string;
  firstName?: string;
  lastName?: string;
  phone?: string;
  email?: string;
  qualification?: string;
  geoLocation?: GeoLocation;
  address?: Address;
  specialities?: Specialities;
  license?: License;
  licensesData?: LicenseData[];
  onboardingFlags?: AgentOnboardingFlags;
  socialSecurityNumber?: string;
  fullSocialSecurityNumber?: string;
  noCallNoShow?: number;
  attendance?: AgentAttendance;
  requirements?: AgentRequirement[];
  profileImageUrl?: string;
  rating?: AgentRating;
  stage?: AgentStage;
  otherQualification?: string;
  referralCode?: string;
  showReferralAlert?: boolean;
  rate?: number;
  uploads?: AgentUploads[];
  isFirstSession?: boolean;
  isNotificationAlertShown?: boolean;
  isFirstTimeLoggedIn?: boolean;
  applicantId?: string;
  signupExperimentId?: string;
  signupConfig?: {
    signingBonusAmount1?: number;
    signingBonusAmount2?: number;
  };
  hasClaimedBonus?: boolean;
  notificationPreferences?: NotificationPreference;
  enableNewFlow?: boolean;
  appRatingStatus?: AppRating;
  status?: number;
  phoneNumber?: string | number;
  onboardAt?: string;
  lastLogAt?: string;
  preference?: Preference;
  interestedAt?: Record<string, unknown>;
  canBeOnboardedInStripe?: boolean;
  alreadyOnboarded?: boolean;
  dob?: string;
  paymentAccountInfo?: PaymentAccountInfo;
  signedDate?: string;
  tmz?: string;
  alreadyWorked?: boolean;
  referralCodeUsed?: {
    code: string;
    createdAt: Date;
    referredBonusId?: string;
    referrerBonusId?: string;
    referrer: string;
  };
  signedContractorAgreements?: ContractorAgreement[];
  attendancePolicy?: {
    callOffsCancellations: number;
    standardCancellations: number;
    isnewAttendancePolicyFeatureFlagEnabled: boolean;
  };
  shouldSignLatestAgreement?: boolean;
  backgroundCheckApplications?: BackgroundCheckApplication[];
  mobile?: Mobile;
  baseRate?: number;
}

export type SignUpStatus = "inprogress" | "complete" | "unstarted" | "unknown";
